// ─── Lot Types ──────────────────────────────────────────────

export interface LotType {
  id: string;
  name: string;
  code: string;
  category: string;
  description?: string;
  defaultWidth: number;
  defaultHeight: number;
  capacity: number;
  basePrice: number;
  color: string;
  icon?: string;
  imageUrl?: string;
  isActive: boolean;
}

// ─── Section ────────────────────────────────────────────────

export type LayoutMode = 'GRID' | 'FREEFORM' | 'HYBRID';

export interface Section {
  id: string;
  name: string;
  label: string;
  description?: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  layoutMode: LayoutMode;
  rows: number;
  cols: number;
  lotWidth: number;
  lotHeight: number;
  lotGap: number;
  phase?: string;
  category: string;
  isActive: boolean;
  sortOrder: number;
  color?: string;
}

// ─── Lot ────────────────────────────────────────────────────

export type LotStatus = 'AVAILABLE' | 'RESERVED' | 'SOLD' | 'MAINTENANCE' | 'LOCKED' | 'BLOCKED';

export interface Lot {
  id: string;
  sectionId: string;
  lotTypeId?: string;
  lotType?: LotType;
  lotNumber: string;
  price: number;
  status: LotStatus;
  row: number;
  col: number;
  width?: number;
  height?: number;
  x_coord: number;
  y_coord: number;
  isFreeform: boolean;
  orientation: string;
  isLocked: boolean;
  lockedReason?: string;
  lockedUntil?: string;
  occupantName?: string;
  intermentDate?: string;
  expiryDate?: string;
  notes?: string;
  createdAt: string;
}

// ─── Audit Log ──────────────────────────────────────────────

export interface LotAuditLog {
  id: string;
  lotId: string;
  action: string;
  fromValue?: string;
  toValue?: string;
  performedBy?: string;
  reason?: string;
  createdAt: string;
}

// ─── Policy ─────────────────────────────────────────────────

export interface InventoryPolicy {
  id: string;
  key: string;
  label: string;
  description?: string;
  value: string;
  category: string;
  valueType: string;
  options?: string;
  isActive: boolean;
}

// ─── Status Config ──────────────────────────────────────────

export const STATUS_CONFIG: Record<LotStatus, { label: string; color: string; bg: string; icon: string }> = {
  AVAILABLE:   { label: 'Available',   color: '#22C55E', bg: 'rgba(34,197,94,0.15)',  icon: 'solar:check-circle-bold-duotone' },
  RESERVED:    { label: 'Reserved',    color: '#F59E0B', bg: 'rgba(245,158,11,0.15)', icon: 'solar:clock-circle-bold-duotone' },
  SOLD:        { label: 'Sold',        color: '#EF4444', bg: 'rgba(239,68,68,0.15)',  icon: 'solar:sale-bold-duotone' },
  MAINTENANCE: { label: 'Maintenance', color: '#8B5CF6', bg: 'rgba(139,92,246,0.15)', icon: 'solar:settings-bold-duotone' },
  LOCKED:      { label: 'Locked',      color: '#64748B', bg: 'rgba(100,116,139,0.15)', icon: 'solar:lock-bold-duotone' },
  BLOCKED:     { label: 'Blocked',     color: '#334155', bg: 'rgba(51,65,85,0.15)',   icon: 'solar:forbidden-circle-bold-duotone' },
};

export const CATEGORY_CONFIG: Record<string, { label: string; icon: string }> = {
  LAWN:              { label: 'Lawn Lots',        icon: 'solar:sun-bold-duotone' },
  MAUSOLEUM:         { label: 'Mausoleum',        icon: 'solar:buildings-bold-duotone' },
  COLUMBARIUM:       { label: 'Columbarium',      icon: 'solar:archive-bold-duotone' },
  GARDEN_ESTATE:     { label: 'Garden Estate',    icon: 'solar:tree-bold-duotone' },
  MEMORIAL_WALL:     { label: 'Memorial Wall',    icon: 'solar:wallpaper-bold-duotone' },
  OSSUARY:           { label: 'Ossuary',          icon: 'solar:box-bold-duotone' },
  CHAPEL_CRYPT:      { label: 'Chapel Crypt',     icon: 'solar:home-2-bold-duotone' },
  SCATTERING_GARDEN: { label: 'Scattering Garden', icon: 'solar:leaf-bold-duotone' },
  PREMIUM:           { label: 'Premium/VIP',      icon: 'solar:star-bold-duotone' },
};

// ─── Helpers ────────────────────────────────────────────────

export const fmt = (n: number) => `₱${n.toLocaleString()}`;
export const formatDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
